import { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import Text from '@hiredigital/ui/Text/Text';
import Button from '@hiredigital/ui/Button';

import { sendEvent } from '@lib/gtm.js';
import Image from 'next/image';
import TextInput from '@hiredigital/ui/Input/SimpleTextInput';
import { postInterest } from '@apis/interest';
import { validateEmail } from '@helpers/validator';
import { submitHubspotForm } from '@components/Registration/common/helpers';
import CurveLineMaker from '@components/CurveLineMaker/index.jsx';

import Styles from './BlogSubscribe.module.scss';

const defaultPayload = {
  join_list: true,
  form_type: 'Email Subscribe',
  form_variant: 'Content CTA',
};

const Subscribe = forwardRef(({ aside }, ref) => {
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleEmailChange = (e) => {
    setErrors({});
    setEmail(e.target.value);
  };

  const handleSubmit = () => {
    if (validateEmail(email)) {
      onSubscribe?.({ email, ...defaultPayload });
    } else {
      setErrors({ email: 'Enter a valid email.' });
    }
  };

  const handleKeyPress = (e) => e.key === 'Enter' && handleSubmit();

  const onSubscribe = (data) => {
    const pageName = document.title;
    const pageUrl = window.location.href;

    setLoading(true);
    const updatedData = {
      ...data,
      ...router.query,
      site: 'Hire Digital',
    };
    submitHubspotForm(process.env.NEXT_PUBLIC_NEWSLETTER_FORMID, pageUrl, pageName, updatedData);

    postInterest(data).then((response) => {
      if (response.data) {
        sendEvent({ event: 'GTM-Subscription' });
        if (window.fbq) {
          window.fbq('track', 'CompleteRegistration');
        }
        setMessageSent(true);
        setEmail('');

        setTimeout(() => {
          setLoading(false);
          setMessageSent(false);
        }, 5000); // wait 5 seconds, then reset to false
      }
    });
  };

  let successMessage, submitBtn;
  if (messageSent) {
    if (aside) {
      successMessage = <div className={Styles.successMessage}>{`Thanks for subscribing.`}</div>;
    } else {
      successMessage = (
        <Text element='h3' color={Text.Color.WHITE}>
          {`Thanks for subscribing.`}
        </Text>
      );
    }
  } else {
    submitBtn = (
      <>
        <TextInput
          type='email'
          name='email'
          label={`Your Email`}
          placeholder='Email Address'
          value={email}
          error={errors?.email}
          onKeyPress={handleKeyPress}
          onChange={handleEmailChange}
        />
        <Button
          type={Button.Type.DARKBLUE}
          {...(aside && { size: Button.Size.HALFWIDTH })}
          className={classNames({ [Styles.btnInline]: !aside }, Styles.button)}
          onClick={handleSubmit}
          isLoading={loading}
          name='subscribe'
          style={{ flexShrink: 0 }}>
          {`Subscribe`}
        </Button>
      </>
    );
  }
  if (aside) {
    return (
      <div ref={ref} className={Styles.panelAside}>
        <div className={Styles.itemContainer}>
          <div className={Styles.rightAngleLarge3}></div>
        </div>
        <Image
          className={Styles.image}
          src={`/img/airplane.svg`}
          alt='image'
          height={36}
          width={36}
        />
        <h3 className={Styles.asideTitle}>
          <CurveLineMaker
            sentence={`Receive insights from Hire Digital for free.`}
            underlineText={`for free`}
          />
        </h3>
        <Text
          className={Styles.notice}
          type={Text.Type.PRIMARY}
          color={Text.Color.WHITE}>{`Subscribe today`}</Text>
        {messageSent ? (
          <div style={{ textAlign: 'center' }}>{successMessage}</div>
        ) : (
          <div className={Styles.formAside} data-test-id='subs-form'>
            {submitBtn}
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={ref} className={Styles.panel}>
      <div className={Styles.panelInner}>
        <div className={Styles.container}>
          <Image
            className={classNames(Styles.image, Styles.large)}
            src={`/img/airplane.svg`}
            alt='image'
            height={36}
            width={36}
          />
          <div className={Styles.text}>
            <Text
              element='h3'
              className={Styles.title}
              size={Text.Size.SUBHEADLINE_LARGE}
              color={Text.Color.WHITE}>
              <CurveLineMaker sentence={`Join 2,500+ subscribers`} underlineText={`2,500+`} />
            </Text>
            <Text color={Text.Color.WHITE}>
              {`Join thousands of other marketers and digital professionals and stay ahead of marketing, digital and content trends.`}
            </Text>
          </div>
        </div>
        <div className={Styles.formContainer} data-test-id='subs-form'>
          {messageSent ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>{successMessage}</div>
          ) : (
            <div className={Styles.form}>{submitBtn}</div>
          )}
        </div>
      </div>
    </div>
  );
});

Subscribe.propTypes = {
  aside: PropTypes.bool,
  router: PropTypes.object,
};

export default Subscribe;
